import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Clock, ArrowLeft } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared header/footer chrome for legal pages (Terms, Privacy). `sections` is
 * an array of { heading, body } for the current language, `body` being an
 * array of paragraph/list strings or {list: string[]} blocks.
 */
const LegalLayout = ({ title, effectiveDate, sections }) => {
  const { language, toggleLanguage } = useLanguage();
  const isEs = language === 'es';
  const [appName, setAppName] = useState('Regulisto');

  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        if (response.data?.app_name) setAppName(response.data.app_name);
      } catch (error) {
        console.error('Error fetching public settings:', error);
      }
    };
    fetchAppName();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={toggleLanguage} className="text-slate-600">
              {isEs ? '🇬🇧 EN' : '🇪🇸 ES'}
            </Button>
            <Link to="/">
              <Button variant="ghost" className="gap-2 text-slate-600">
                <ArrowLeft className="w-4 h-4" />
                {isEs ? 'Volver' : 'Back'}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {isEs ? 'Última actualización: ' : 'Last updated: '}{effectiveDate}
        </p>

        <div className="mt-10 space-y-10">
          {sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900 mb-3">
                {section.heading}
              </h2>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                {section.body.map((block, j) => {
                  if (typeof block === 'string') return <p key={j}>{block}</p>;
                  if (block.list) {
                    return (
                      <ul key={j} className="list-disc pl-5 space-y-1">
                        {block.list.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ul>
                    );
                  }
                  // Anything else (e.g. a paragraph with an inline <Link>) is
                  // rendered as-is inside a <p>, for content richer than plain text.
                  return <p key={j}>{block}</p>;
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-center text-sm text-slate-400">
          © {new Date().getFullYear()} {appName}
        </div>
      </footer>
    </div>
  );
};

export default LegalLayout;
